import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import FormCard from '../../components/FormCard';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Table from '../../components/Table';
import { useEpisodeList } from '../../hooks/useEpisodeDbCache';
import { EpisodeListItem } from '../../api/episodeDbCache';

function toIsoDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function defaultFrom(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 7);
  return toIsoDateString(d);
}

function defaultTo(): string {
  return toIsoDateString(new Date());
}

function isoBound(date: string, end: boolean): string {
  return end ? `${date}T23:59:59Z` : `${date}T00:00:00Z`;
}

export default function EpisodesListView(): ReactElement {
  const [from, setFrom] = useState<string>(defaultFrom());
  const [to, setTo] = useState<string>(defaultTo());
  const navigate = useNavigate();

  const { data, isPending, isError, error, refetch, isFetching } = useEpisodeList(
    isoBound(from, false),
    isoBound(to, true),
  );

  const columns = useMemo<ColumnDef<EpisodeListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'cached',
        header: '',
        cell: ({ row }) => (row.original.cached ? '' : '★'),
        size: 24,
      },
      {
        accessorKey: 'captured_ts',
        header: 'Captured (UTC)',
        cell: ({ row }) => row.original.captured_ts.replace('T', ' ').slice(0, 19),
      },
      { accessorKey: 'filename', header: 'Filename' },
      { accessorKey: 'device_id', header: 'Device ID' },
    ],
    [],
  );

  const handleRowClick = (_e: React.MouseEvent, row: EpisodeListItem) => {
    navigate(`/admin/episodes/detail/${row.s3_key}`);
  };

  return (
    <>
      <SectionHeader title="Episode DB Viewer" isLoading={isFetching} />

      <FormCard>
        <div className="cr-flex cr-flex-col cr-gap-3 cr-p-3 md:cr-flex-row md:cr-items-end">
          <label className="cr-flex cr-flex-col">
            <span className="cr-text-xs cr-font-medium cr-text-gray-500">From</span>
            <input
              type="date"
              className="cr-mt-1 cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="cr-flex cr-flex-col">
            <span className="cr-text-xs cr-font-medium cr-text-gray-500">To</span>
            <input
              type="date"
              className="cr-mt-1 cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <button
            className="cr-rounded-md cr-bg-blue-600 cr-px-3 cr-py-2 cr-text-sm cr-font-medium cr-text-white hover:cr-bg-blue-700"
            onClick={() => refetch()}
          >
            Refresh
          </button>
          <span className="cr-text-xs cr-text-gray-500">★ = not yet cached</span>
        </div>
      </FormCard>

      {isPending && <Spinner isFetching />}

      {isError && (
        <p className="cr-mt-2 cr-text-sm cr-text-red-600">
          Failed to load episodes: {(error as Error)?.message ?? 'unknown error'}
        </p>
      )}

      {!isPending && !isError && (
        <Table<EpisodeListItem>
          data={data ?? []}
          columns={columns}
          onRowClick={handleRowClick}
        />
      )}
    </>
  );
}
